import { Badge, Button, Modal, Surface } from '@chopchop-se/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Supplier, SupplierCreate, SupplierDetail } from '@warehouse/shared';
import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';

import { usePermission } from '../../../auth/auth-store';
import { DataTable, type DataTableColumn } from '../../../components/DataTable';
import { FullPageSpinner } from '../../../components/FullPageSpinner';
import { PageHeader } from '../../../components/PageHeader';
import { useListState } from '../../../hooks/useListState';
import { listQueryKey } from '../../../lib/list-query';
import { toastError, toastSuccess } from '../../../lib/notify';
import { useLiveQuery } from '../../../realtime/useLiveQuery';
import { createSupplier, getSupplier, getSupplierHistory, listSuppliers, updateSupplier } from '../api';
import { BcBadge, BcSyncNotice, isBcLocked } from '../components/BcBadge';
import { EntityHistory } from '../components/EntityHistory';
import { LoadFailure } from '../components/LoadFailure';
import { CheckboxField, FormSection, TextField } from '../components/form';
import { Tabs, useTabState } from '../components/Tabs';

interface SupplierFormValues {
  name: string;
  code: string;
  orgNo: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  addressLine1: string;
  addressLine2: string;
  postalCode: string;
  city: string;
  countryCode: string;
  isActive: boolean;
  desadvEnabled: boolean;
}

const EMPTY: SupplierFormValues = {
  name: '',
  code: '',
  orgNo: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  addressLine1: '',
  addressLine2: '',
  postalCode: '',
  city: '',
  countryCode: '',
  isActive: true,
  desadvEnabled: false,
};

const nullable = (value: string): string | null => (value.trim().length === 0 ? null : value.trim());

function toPayload(values: SupplierFormValues): SupplierCreate {
  return {
    name: values.name.trim(),
    code: nullable(values.code),
    orgNo: nullable(values.orgNo),
    contactName: nullable(values.contactName),
    contactEmail: nullable(values.contactEmail),
    contactPhone: nullable(values.contactPhone),
    addressLine1: nullable(values.addressLine1),
    addressLine2: nullable(values.addressLine2),
    postalCode: nullable(values.postalCode),
    city: nullable(values.city),
    countryCode: nullable(values.countryCode),
    isActive: values.isActive,
    desadvEnabled: values.desadvEnabled,
  } as SupplierCreate;
}

/** Supplier register (spec §5.12). Suppliers are BC vendors. */
export function SuppliersPage(): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const navigate = useNavigate();
  const { state, setState } = useListState({ defaultSort: 'name', defaultDir: 'asc' });
  const canManage = usePermission('registers.suppliers.manage');
  const [creating, setCreating] = useState(false);

  const { data, isLoading, error } = useQuery({
    queryKey: ['master-data', 'suppliers', listQueryKey(state)],
    queryFn: () => listSuppliers(state),
  });
  useLiveQuery(['suppliers'], ['master-data', 'suppliers']);

  const columns: DataTableColumn<Supplier>[] = [
    { id: 'name', header: t('masterData:suppliers.columns.name'), sortField: 'name', cell: (row) => row.name },
    {
      id: 'code',
      header: t('masterData:suppliers.columns.code'),
      sortField: 'code',
      cell: (row) => <span className="font-mono text-xs">{row.code ?? '—'}</span>,
    },
    {
      id: 'bcVendorNo',
      header: t('masterData:suppliers.columns.bcVendorNo'),
      hideBelow: 'md',
      cell: (row) => (row.bcVendorNo ? <BcBadge bc={{ synced: true, bcNo: row.bcVendorNo }} /> : '—'),
    },
    {
      id: 'orgNo',
      header: t('masterData:suppliers.columns.orgNo'),
      hideBelow: 'xl',
      cell: (row) => row.orgNo ?? '—',
    },
    { id: 'city', header: t('masterData:suppliers.columns.city'), sortField: 'city', hideBelow: 'lg', cell: (row) => row.city ?? '—' },
    {
      id: 'email',
      header: t('masterData:suppliers.columns.contactEmail'),
      hideBelow: 'xl',
      cell: (row) => row.contactEmail ?? '—',
    },
    {
      id: 'active',
      header: t('masterData:suppliers.columns.active'),
      sortField: 'isActive',
      cell: (row) =>
        row.isActive ? (
          <Badge tone="success">{t('common:common.active')}</Badge>
        ) : (
          <Badge tone="warning">{t('common:common.inactive')}</Badge>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('masterData:suppliers.title')}
        subtitle={t('masterData:suppliers.subtitle')}
        actions={
          canManage ? (
            <Button
              icon={<Plus />}
              onClick={() => {
                setCreating(true);
              }}
            >
              {t('masterData:suppliers.new')}
            </Button>
          ) : null
        }
      />

      <DataTable
        columns={columns}
        rows={data?.items ?? []}
        rowKey={(row) => row.id}
        state={state}
        onStateChange={setState}
        total={data?.total ?? null}
        loading={isLoading}
        error={error}
        searchPlaceholder={t('masterData:suppliers.searchPlaceholder')}
        emptyTitle={t('masterData:suppliers.empty')}
        emptyDescription={t('masterData:suppliers.emptyDescription')}
        onRowClick={(row) => {
          void navigate(`/registers/suppliers/${row.id}`);
        }}
      />

      {creating ? (
        <SupplierModal
          onClose={() => {
            setCreating(false);
          }}
          onCreated={(supplier) => {
            setCreating(false);
            void navigate(`/registers/suppliers/${supplier.id}`);
          }}
        />
      ) : null}
    </>
  );
}

function SupplierModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (supplier: SupplierDetail) => void;
}): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (values: SupplierFormValues) => createSupplier(toPayload(values)),
    onSuccess: (supplier) => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      toastSuccess(t('masterData:suppliers.created'));
      onCreated(supplier);
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  return (
    <Modal open onClose={onClose} title={t('masterData:suppliers.new')} width={760}>
      <SupplierForm
        defaultValues={EMPTY}
        submitting={mutation.isPending}
        onCancel={onClose}
        onSubmit={(values) => {
          mutation.mutate(values);
        }}
      />
    </Modal>
  );
}

/** Supplier detail: Detaljer + Historik. */
export function SupplierDetailPage(): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const { id = '' } = useParams();
  const [tab, setTab] = useTabState('details');
  const canManage = usePermission('registers.suppliers.manage');
  const queryClient = useQueryClient();

  const {
    data: supplier,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['master-data', 'supplier', id],
    queryFn: () => getSupplier(id),
    enabled: Boolean(id),
  });
  useLiveQuery(['suppliers'], ['master-data', 'supplier', id]);

  const mutation = useMutation({
    mutationFn: (values: SupplierFormValues) => updateSupplier(id, toPayload(values)),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['master-data'] });
      toastSuccess(t('masterData:suppliers.saved'));
    },
    onError: (error: unknown) => {
      toastError(error);
    },
  });

  if (error) return <LoadFailure error={error} />;
  if (isLoading || !supplier) return <FullPageSpinner />;

  return (
    <>
      <PageHeader
        breadcrumb={<Link to="/registers/suppliers">{t('masterData:suppliers.title')}</Link>}
        title={supplier.name}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {supplier.code ? <Badge tone="neutral">{supplier.code}</Badge> : null}
            <BcBadge bc={supplier.bc} />
            <Badge tone="neutral" size="sm">
              {t('masterData:suppliers.stats.articles')}: {supplier.articleCount}
            </Badge>
            <Badge tone="neutral" size="sm">
              {t('masterData:suppliers.stats.barcodes')}: {supplier.barcodeCount}
            </Badge>
          </div>
        }
      />

      <Tabs
        tabs={[
          { id: 'details', label: t('masterData:suppliers.tabs.details') },
          { id: 'history', label: t('masterData:suppliers.tabs.history') },
        ]}
        active={tab}
        onChange={setTab}
      />

      {tab === 'details' ? (
        <Surface className="p-6">
          <SupplierForm
            defaultValues={{
              name: supplier.name,
              code: supplier.code ?? '',
              orgNo: supplier.orgNo ?? '',
              contactName: supplier.contactName ?? '',
              contactEmail: supplier.contactEmail ?? '',
              contactPhone: supplier.contactPhone ?? '',
              addressLine1: supplier.addressLine1 ?? '',
              addressLine2: supplier.addressLine2 ?? '',
              postalCode: supplier.postalCode ?? '',
              city: supplier.city ?? '',
              countryCode: supplier.countryCode ?? '',
              isActive: supplier.isActive,
              desadvEnabled: supplier.desadvEnabled,
            }}
            bc={supplier.bc}
            readOnly={!canManage}
            submitting={mutation.isPending}
            onSubmit={(values) => {
              mutation.mutate(values);
            }}
          />
        </Surface>
      ) : (
        <EntityHistory
          queryKey={['master-data', 'supplier', id, 'history']}
          fetch={() => getSupplierHistory(id)}
          topics={['suppliers']}
        />
      )}
    </>
  );
}

function SupplierForm({
  defaultValues,
  bc,
  readOnly = false,
  submitting,
  onSubmit,
  onCancel,
}: {
  defaultValues: SupplierFormValues;
  bc?: SupplierDetail['bc'];
  readOnly?: boolean;
  submitting: boolean;
  onSubmit: (values: SupplierFormValues) => void;
  onCancel?: () => void;
}): ReactNode {
  const { t } = useTranslation(['masterData', 'common']);
  const { register, handleSubmit, formState } = useForm<SupplierFormValues>({ defaultValues });

  return (
    <form
      onSubmit={(event) => {
        void handleSubmit(onSubmit)(event);
      }}
      className="flex flex-col gap-7"
      noValidate
    >
      {bc ? <BcSyncNotice bc={bc} /> : null}

      <FormSection title={t('masterData:suppliers.sections.identity')}>
        <TextField
          label={t('masterData:suppliers.fields.name')}
          disabled={readOnly || isBcLocked(bc, 'name')}
          hint={isBcLocked(bc, 'name') ? t('masterData:bc.fieldHint') : undefined}
          error={formState.errors.name ? t('common:common.required') : undefined}
          {...register('name', { required: true })}
        />
        <TextField
          label={t('masterData:suppliers.fields.code')}
          hint={t('masterData:suppliers.hints.code')}
          disabled={readOnly}
          {...register('code')}
        />
        <TextField label={t('masterData:suppliers.fields.orgNo')} disabled={readOnly} {...register('orgNo')} />
        <CheckboxField
          label={t('masterData:suppliers.fields.isActive')}
          disabled={readOnly}
          {...register('isActive')}
        />
      </FormSection>

      <FormSection title={t('masterData:suppliers.sections.contact')}>
        <TextField label={t('masterData:suppliers.fields.contactName')} disabled={readOnly} {...register('contactName')} />
        <TextField
          label={t('masterData:suppliers.fields.contactEmail')}
          type="email"
          disabled={readOnly}
          {...register('contactEmail')}
        />
        <TextField label={t('masterData:suppliers.fields.contactPhone')} disabled={readOnly} {...register('contactPhone')} />
      </FormSection>

      <FormSection title={t('masterData:suppliers.sections.address')}>
        <TextField label={t('masterData:suppliers.fields.addressLine1')} disabled={readOnly} {...register('addressLine1')} />
        <TextField label={t('masterData:suppliers.fields.addressLine2')} disabled={readOnly} {...register('addressLine2')} />
        <TextField label={t('masterData:suppliers.fields.postalCode')} disabled={readOnly} {...register('postalCode')} />
        <TextField label={t('masterData:suppliers.fields.city')} disabled={readOnly} {...register('city')} />
        <TextField label={t('masterData:suppliers.fields.countryCode')} disabled={readOnly} {...register('countryCode')} />
      </FormSection>

      <FormSection title={t('masterData:suppliers.sections.options')}>
        <CheckboxField
          label={t('masterData:suppliers.fields.desadvEnabled')}
          hint={t('masterData:suppliers.hints.desadvEnabled')}
          disabled={readOnly}
          {...register('desadvEnabled')}
        />
      </FormSection>

      {readOnly ? null : (
        <div className="flex justify-end gap-2">
          {onCancel ? (
            <Button type="button" variant="ghost" onClick={onCancel}>
              {t('common:actions.cancel')}
            </Button>
          ) : null}
          <Button type="submit" loading={submitting}>
            {t('common:actions.save')}
          </Button>
        </div>
      )}
    </form>
  );
}
